import { useEffect, useRef, useState } from 'react'
import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import invariant from 'tiny-invariant'
import { useTranslation } from '@kostbar/i18n'
import { GripVertical, X } from 'lucide-react'
import type { Category, Product } from '@/api/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'

function DraggableProductRow({
  product,
  canEdit,
}: {
  product: Product
  canEdit: boolean
}) {
  const { t: tPosLayout } = useTranslation('pos-layout')
  const productRef = useRef<HTMLDivElement>(null)
  const [dragging, setDragging] = useState(false)
  const price = product.openPrice
    ? tPosLayout('tileState.openPrice')
    : product.currentPrice !== undefined && product.currentPrice > 0
      ? `€${(product.currentPrice / 100).toFixed(2)}`
      : tPosLayout('values.priceEmpty')
  const isInactive = !product.active

  useEffect(() => {
    if (!canEdit) return
    const el = productRef.current
    invariant(el)
    return draggable({
      element: el,
      // Carry the facts the library already has. The drop handler must not re-resolve them from a
      // catalogue lookup that may not have answered yet.
      getInitialData: () => ({
        type: 'product',
        sku: product.sku,
        name: product.name,
        active: product.active,
      }),
      onDragStart: () => setDragging(true),
      onDrop: () => setDragging(false),
    })
  }, [product, canEdit])

  return (
    <div
      ref={productRef}
      className={`flex items-center gap-3 py-2 text-sm transition-opacity ${
        dragging ? 'opacity-60' : ''
      } ${isInactive ? 'opacity-50' : ''}`}
    >
      <GripVertical
        className={`h-4 w-4 shrink-0 ${canEdit ? 'text-muted-foreground' : 'text-muted-foreground/40'}`}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-medium truncate min-w-0">{product.name}</span>
          {isInactive && (
            <Badge variant="outline" className="text-[10px] px-1 py-0 shrink-0">
              {tPosLayout('tileState.inactive')}
            </Badge>
          )}
        </div>
        <div className="text-xs text-muted-foreground truncate">
          {tPosLayout('labels.sku')} {product.sku}
        </div>
      </div>
      <div className="text-sm font-semibold tabular-nums shrink-0">{price}</div>
    </div>
  )
}

interface ProductLibraryProps {
  open: boolean
  onClose: () => void
  products: Array<Product>
  total: number
  categories: Array<Category>
  loading: boolean
  searchQuery: string
  onSearchChange: (query: string) => void
  selectedCategory: string
  onCategoryChange: (category: string) => void
  showInactive: boolean
  onShowInactiveChange: (show: boolean) => void
  onLoadMore: () => void
  /** The server clamped the page: loading more cannot reveal the rest, only searching can. */
  truncated?: boolean
  canEdit: boolean
}

export function ProductLibrary({
  open,
  onClose,
  products,
  total,
  categories,
  loading,
  searchQuery,
  onSearchChange,
  selectedCategory,
  onCategoryChange,
  showInactive,
  onShowInactiveChange,
  onLoadMore,
  truncated = false,
  canEdit,
}: ProductLibraryProps) {
  const { t: tPosLayout } = useTranslation('pos-layout')
  const hasMore = products.length < total && !truncated

  return (
    <aside
      aria-hidden={!open}
      className={`flex h-full flex-col rounded-xl border bg-card shadow-sm transition-all duration-300 ${
        open
          ? 'opacity-100 translate-x-0'
          : 'pointer-events-none opacity-0 translate-x-6'
      }`}
    >
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div>
          <div className="text-sm font-semibold">
            {tPosLayout('labels.libraryTitle')}
          </div>
          <div className="text-xs text-muted-foreground">
            {tPosLayout('labels.librarySubtitle')}
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          aria-label={tPosLayout('actions.closeLibraryAria')}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      <div className="p-4 space-y-3">
        <Input
          value={searchQuery}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={tPosLayout('labels.searchPlaceholder')}
        />
        <Select value={selectedCategory} onValueChange={onCategoryChange}>
          <SelectTrigger>
            <SelectValue placeholder={tPosLayout('labels.allCategories')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {tPosLayout('labels.allCategories')}
            </SelectItem>
            {categories.map((category) => (
              <SelectItem key={category.code} value={category.code}>
                {category.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center justify-between">
          <Label htmlFor="show-inactive" className="text-xs">
            {tPosLayout('labels.showInactive')}
          </Label>
          <Switch
            id="show-inactive"
            checked={showInactive}
            onCheckedChange={onShowInactiveChange}
          />
        </div>
      </div>

      <div className="flex-1 min-h-0 pb-4">
        <ScrollArea className="h-full overflow-hidden">
          <div className="px-4">
            <div className="grid grid-cols-[1fr_auto] gap-2 text-xs uppercase text-muted-foreground">
              <span>{tPosLayout('labels.product')}</span>
              <span className="text-right">{tPosLayout('labels.price')}</span>
            </div>
            <div className="mt-2">
              {loading ? (
                <div className="text-sm text-muted-foreground py-4">
                  {tPosLayout('labels.loadingProducts')}
                </div>
              ) : products.length === 0 ? (
                <div className="text-sm text-muted-foreground py-4">
                  {tPosLayout('labels.noProducts')}
                </div>
              ) : (
                <>
                  <div className="divide-y divide-border/60">
                    {products.map((product) => (
                      <DraggableProductRow
                        key={product.sku}
                        product={product}
                        canEdit={canEdit}
                      />
                    ))}
                  </div>
                  {hasMore && (
                    <div className="pt-3 pb-1">
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={onLoadMore}
                      >
                        {tPosLayout('actions.loadMore', {
                          shown: products.length,
                          total,
                        })}
                      </Button>
                    </div>
                  )}
                  {truncated && (
                    <div className="pt-3 pb-1 text-xs text-muted-foreground">
                      {tPosLayout('labels.libraryTruncated', {
                        shown: products.length,
                        total,
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </ScrollArea>
      </div>
    </aside>
  )
}
